import React, { useState, useMemo, useRef } from "react";
import { useClasseContext } from "../context/ClasseContext";
import { usePostApi } from "../hooks/post_api_hook/usePostApi";
import { useGroupe } from "../hooks/fetch_api_hook/useGroupe";
import AddGroupe from "../components/VueGroupe/AdminGroupe/ComponentAdminGroupe/Addgroupe";
import { useDeleteGroupe } from "../hooks/delete_api_hook/useDeleteGroupe";
import EditGroupe from "../components/VueGroupe/AdminGroupe/EditGroupe";
import useUpdateGroupe from "../hooks/update_api_hook/useUpdateGroupe";
import DetailGroupe from "../components/VueGroupe/AdminGroupe/DetailGroupe";
import useSortableData from "../hooks/personnaliser/useSortableData";
import Loading from "../components/general/Loading";
import { url } from "../utils/confserveur";
import { useUser } from "../context/UserContext";
import { Plus, Layers, LayoutGrid, Table, Upload } from "lucide-react";
import useConfirm from "../hooks/ui/useConfirm";
import useCardTableView from "../hooks/ui/useCardTableView";
import ImportElevesGroupe from "../components/VueGroupe/AdminGroupe/ComponentAdminGroupe/ImportElevesGroupe";
import TableGroupe from "../components/VueGroupe/AdminGroupe/ComponentAdminGroupe/TableGroupe";
import CardGroup from "../components/VueGroupe/AdminGroupe/ComponentAdminGroupe/CardGroup";
import { filtrerGroupesDeBesoin } from "../utils/groupesDeBesoin";

function AdminGroupe() {
  const [addClick, setAddClick] = useState(false);
  const { etablissementData, user } = useUser();
  const [reload, setReload] = useState(false);

  const { deleteGroupe } = useDeleteGroupe();

  const [{ isLoading: isLoadingPost, isError: isErrorPost }, doPost] = usePostApi(url + "groupes/");
  const { data: listGroupes, isLoading: isLoadingGroupe } = useGroupe(`?etablissement_id=${etablissementData.id}`, null, reload);

  // Tout ce que la page affiche ou modifie porte sur les GROUPES DE BESOIN
  // (est_groupe_de_besoin, servi par le back : au moins deux classes). Les
  // demi-groupes (une seule classe, créés par l'ENT, partagés entre profs) n'y
  // figurent pas : ni dans le tableau, ni dans les compteurs, ni dans « tout
  // verrouiller », ni comme cible d'un changement de groupe, ni dans l'import
  // (depuis le 2026-09-30, voir plus bas).
  const groupesDeBesoin = useMemo(() => filtrerGroupesDeBesoin(listGroupes), [listGroupes]);

  const [{ data: GroupPosted, isLoading: isLoadingUpdateEleveToGroupe }, updateEleveToGroupe] = usePostApi(url + "groupes/affecter-eleves/");
  const { updateGroupe, isLoading: isLoadingUpdate } = useUpdateGroupe();

  const divRef = useRef(null);

  const { items: sortedGroupes, requestSort, sortConfig } = useSortableData(groupesDeBesoin);

  const [editMode, setEditMode] = useState(null);
  const [editingGroupe, setEditingGroupe] = useState({ nom: '', professeur: '', niveau: '', id: null });

  const [detailGroupeMode, setDetailGroupeMode] = useState(false);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const [nomGroupe, setNomGroupe] = useState("");
  const [professeur, setProfesseur] = useState('');
  const [niveau, setNiveau] = useState('6eme');
  const [selectedClasses, setSelectedClasses] = useState([]);

  const { view: currentView, setView: setCurrentView, isMobile } = useCardTableView();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('all');
  const [selectedStatut, setSelectedStatut] = useState('all');
  const [selectedBarrette, setSelectedBarrette] = useState('all');
  const [selectedMatiere, setSelectedMatiere] = useState('all');

  // Délégué d'affectation : voit toutes les matières → besoin du filtre matière
  const isDelegueAffectation = !!etablissementData?.professeurs?.find(p => p.id === user?.id)?.delegue_affectation;
  const [editedProfesseur, setEditedProfesseur] = useState('');
  const [editedNomGroupe, setEditedNomGroupe] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);


  const verifierCohérenceGroupe = (matiereCourante) => {
    const classesNoms = selectedClasses
      .map(id => {
        const classe = etablissementData.classes.find(c => c.id === id);
        return classe ? classe.nom : null;
      })
      .filter(Boolean);

    const sortedClassesNoms = [...classesNoms].sort();

    // On vérifie uniquement contre les groupes de la même matière (un élève peut
    // appartenir à un groupe maths ET un groupe français pour la même classe).
    // Fallback : si matière inconnue, on garde l'ancien comportement (tous les groupes).
    const groupesAComparer = matiereCourante
      ? sortedGroupes.filter(g => g.matiere === matiereCourante)
      : sortedGroupes;

    const groupeExact = groupesAComparer.find(groupe => {
      const sortedExisting = [...groupe.classes_detail].sort();
      return sortedExisting.length === sortedClassesNoms.length &&
        sortedExisting.every((nom, index) => nom === sortedClassesNoms[index]);
    });

    if (groupeExact) return { ok: true };

    for (let groupe of groupesAComparer) {
      for (let nom of classesNoms) {
        if (groupe.classes_detail.includes(nom)) {
          return { ok: false, message: `La classe "${nom}" est déjà utilisée dans un autre groupe.` };
        }
      }
    }
    return { ok: true };
  };

  const handleAddGroupe = async (item, matiereSelectionnee) => {
    const matiereCourante = isDelegueAffectation ? matiereSelectionnee : user?.matiere;
    const verification = verifierCohérenceGroupe(matiereCourante);
    if (!verification.ok) {
      alert(verification.message);
      return;
    }
    const payload = { ...item, niveau: niveau, classes: selectedClasses };
    await doPost(payload);

    setNomGroupe("");
    setProfesseur("");
    setNiveau("6eme");
    setReload(!reload);
    setAddClick(false);
    setSelectedClasses([]);
    setShowModal(false);
    setEditingGroupe({ nom: '', professeur: '', niveau: '', id: null });
  };

  const handleEdit = (event, groupe) => {
    event.stopPropagation();
    setEditMode(groupe.id);
    setEditedProfesseur(groupe.professeur);
    setEditedNomGroupe(groupe.nom);
    setEditingGroupe({
      nom: groupe.nom,
      professeur: groupe.professeur,
      niveau: groupe.niveau,
      id: groupe.id
    });
  };

  const handleEditMode = () => {
    setEditMode(false);
    setNomGroupe("");
    setEditingGroupe({ nom: '', professeur: '', niveau: '', id: null });
    setProfesseur("");
  };

  const handleDetailGroupeClick = (item) => {
    setDetailGroupeMode(!detailGroupeMode);
    setSelectedGroupe(item);
  };

  const handleUpdateGroupe = async (groupeID) => {
    await updateGroupe(`${url}groupes/${parseInt(groupeID, 10)}/`, {
      nom: editedNomGroupe,
      professeur: editedProfesseur
    });
    setEditMode(false);
    setNomGroupe('');
    setProfesseur('');
    setEditedNomGroupe('');
    setEditedProfesseur('');
    setEditingGroupe({ nom: '', professeur: '', niveau: '', id: null });
    setReload(!reload);
  };

  const handleUpdateEleveGroupe = async (affectations) => {
    const confirmed = await confirm({ title: "Changer ces élèves de groupe ?", message: "Vous allez changer ces élèves de groupe.", confirmLabel: "Continuer", variant: "warning" });
    if (confirmed) await updateEleveToGroupe(affectations);
    setReload(!reload);
    setDetailGroupeMode(!detailGroupeMode);
  };

  const handleUpdateStatut = async (event) => {
    event.stopPropagation();
    // Aucun groupe de besoin (un établissement qui n'a que des demi-groupes) :
    // rien à verrouiller, et `[0]` n'existe pas.
    if (groupesDeBesoin.length === 0) return;
    const groupeIds = groupesDeBesoin.map(groupe => groupe.id);
    const statutCommun = groupesDeBesoin[0].statut;

    // Le message dit la vraie portée : l'action touche TOUS les groupes de
    // besoin (l'ancien singulier « ce groupe » laissait croire à une action
    // locale, et les filtres niveau/barrette ne restreignent rien). Les
    // demi-groupes ne sont jamais touchés : ils restent visibles dans l'espace
    // de leurs profs.
    const nbGroupes = groupeIds.length;
    const confirmed = statutCommun
      ? await confirm({ title: `Verrouiller tous les groupes (${nbGroupes}) ?`, message: `Cette action verrouille vos ${nbGroupes} groupes, y compris ceux masqués par les filtres : on ne pourra plus y attribuer d'élèves.`, confirmLabel: "Tout verrouiller", variant: "warning" })
      : await confirm({ title: `Déverrouiller tous les groupes (${nbGroupes}) ?`, message: `Cette action déverrouille vos ${nbGroupes} groupes, y compris ceux masqués par les filtres : on pourra de nouveau y attribuer des élèves.`, confirmLabel: "Tout déverrouiller", variant: "warning" });
    if (confirmed) {
      await updateGroupe(url + 'groupes/update-statut/', { ids: groupeIds, statut: !statutCommun });
      window.location.reload();
    }
  };

  const handleCancelAdd = () => {
    setAddClick(false);
    setSelectedClasses([]);
  };

  const handleNomGroupeChange = (e) => setNomGroupe(e.target.value);
  const handleProfChange = (e) => setProfesseur(e.target.value);
  const handleRadioChange = (event) => setNiveau(event.target.value);

  const handleCheckClasseChange = (classeId) => {
    setSelectedClasses(prev => prev.includes(classeId) ? prev.filter(id => id !== classeId) : [...prev, classeId]);
  };

  const handleViewStudents = (groupe) => {};

  const handleDelete = async (groupeID) => {
    const confirmed = await confirm({ title: "Supprimer ce groupe ?", confirmLabel: "Supprimer", variant: "danger" });
    if (confirmed) await deleteGroupe(groupeID);
    setReload(!reload);
  };

  const getTotalEleves = () => {
    return groupesDeBesoin.reduce((acc, groupe) => acc + groupe.eleves.length, 0);
  };

  // Extraire les barrettes uniques (combinaisons de classes_detail) : celles des
  // groupes de besoin seulement (un demi-groupe « 6G » n'est pas une barrette).
  const barrettesUniques = useMemo(() => {
    const barrettes = new Set();
    groupesDeBesoin.forEach(groupe => {
      if (groupe.classes_detail && groupe.classes_detail.length > 0) {
        const barretteKey = [...groupe.classes_detail].sort().join(',');
        barrettes.add(barretteKey);
      }
    });
    return Array.from(barrettes).sort();
  }, [groupesDeBesoin]);

  // Filtrage
  const filteredGroupes = groupesDeBesoin.filter(groupe => {
    const matchesSearch = groupe.nom.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNiveau = selectedNiveau === 'all' || groupe.niveau === selectedNiveau;
    const matchesStatut = selectedStatut === 'all' || groupe.statut === (selectedStatut === 'true');
    const matchesBarrette = selectedBarrette === 'all' ||
      (groupe.classes_detail && [...groupe.classes_detail].sort().join(',') === selectedBarrette);
    const matchesMatiere = selectedMatiere === 'all' || groupe.matiere === selectedMatiere;
    return matchesSearch && matchesNiveau && matchesStatut && matchesBarrette && matchesMatiere;
  });

  const handleClose = (result) => {
    setShowModal(false);
  };

  if (isLoadingPost || isLoadingUpdate || isLoadingGroupe || !sortedGroupes) {
    return <div className="flex justify-center items-center h-full"><Loading loadingText={"Chargement de la page admin..."} /></div>;
  }

  if (!detailGroupeMode) {
    return (
      <div className="h-full w-full overflow-y-auto ">
        {ConfirmDialog}
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-sky-600 rounded-t-xl p-4 sm:p-6 xl:p-4 2xl:p-5 3xl:p-6 text-white shadow-lg mx-3 sm:mx-5 mt-3 sm:mt-5">
          <div className="flex flex-col lg:flex-row justify-between gap-4 lg:gap-10 items-start">
            <div>
              <h1 className="text-lg sm:text-xl xl:text-base 2xl:text-lg 3xl:text-2xl font-semibold mb-1 sm:mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2">Configuration des groupes</h1>
              <p className="text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-xl text-blue-100">
                Paramétrage des groupes et affectation initiale des élèves
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 w-full lg:w-auto">
              <button
                onClick={() => setShowImportModal(true)}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30
                  px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-lg font-medium transition-all
                  text-xs sm:text-sm lg:text-lg xl:text-xs 2xl:text-xs 3xl:text-sm duration-200 hover:-translate-y-0.5 flex items-center gap-2"
              >
                <Upload size={18} className="hidden sm:block xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-[18px] 3xl:h-[18px]" />
                IMPORTER ÉLÈVES
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30
                  px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-lg font-medium transition-all
                  text-xs sm:text-sm lg:text-lg xl:text-xs 2xl:text-xs 3xl:text-sm duration-200 hover:-translate-y-0.5 flex items-center gap-2"
              >
                <Plus size={18} className="hidden sm:block xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-[18px] 3xl:h-[18px]" />
                CRÉER UN GROUPE
              </button>

              {groupesDeBesoin.length > 0 && (
                <button
                  onClick={(e) => handleUpdateStatut(e)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30
                    px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-lg font-medium transition-all
                    text-xs sm:text-sm lg:text-lg xl:text-xs 2xl:text-xs 3xl:text-sm duration-200 hover:-translate-y-0.5"
                >
                  {(groupesDeBesoin[0].statut) ? "Verrouiller les groupes" : "Déverouiller les groupes"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-gradient-to-r from-sky-600 to-sky-200 p-3 sm:p-5 xl:p-3 2xl:p-4 3xl:p-5 flex flex-col lg:flex-row justify-between items-center gap-3 lg:gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4 flex-wrap mx-3 sm:mx-5">
          {/* Toggle Vue — caché sur mobile/tablette */}
          {!isMobile && (
            <div className="flex bg-white bg-opacity-20 rounded-full p-1">
              <button
                className={`px-3 sm:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-full text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all ${
                  currentView === 'cards' ? 'bg-white text-indigo-600' : 'text-white opacity-70 hover:opacity-100'
                }`}
                onClick={() => setCurrentView('cards')}
              >
                <LayoutGrid className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 inline mr-1" /> Vue Cartes
              </button>
              <button
                className={`px-3 sm:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-full text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold transition-all ${
                  currentView === 'table' ? 'bg-white text-indigo-600' : 'text-white opacity-70 hover:opacity-100'
                }`}
                onClick={() => setCurrentView('table')}
              >
                <Table className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 inline mr-1" /> Vue Tableau
              </button>
            </div>
          )}

          {/* Filtres */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center w-full lg:w-auto">
            <input
              type="text"
              placeholder="Rechercher un groupe..."
              className="bg-white bg-opacity-90 border-none px-3 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2
                rounded-xl sm:rounded-2xl text-gray-700 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm w-full sm:w-52 xl:w-44 2xl:w-48 3xl:w-52"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="flex gap-2 w-full sm:w-auto">
              <select
                className="bg-white bg-opacity-90 border-none px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2
                  rounded-xl sm:rounded-2xl text-gray-700 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm cursor-pointer flex-1 sm:flex-initial"
                value={selectedNiveau}
                onChange={(e) => {
                  setSelectedNiveau(e.target.value);
                  if (e.target.value !== 'all') setSelectedBarrette('all');
                }}
              >
                <option value="all">Tous les niveaux</option>
                {['6eme', '5eme'].map((niveau) =>
                  <option key={niveau} value={niveau}>{niveau}</option>
                )}
              </select>

              <select
                className="bg-white bg-opacity-90 border-none px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2
                  rounded-xl sm:rounded-2xl text-gray-700 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm cursor-pointer flex-1 sm:flex-initial"
                value={selectedStatut}
                onChange={(e) => setSelectedStatut(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="true">Déverrouillé</option>
                <option value="false">Verrouillé</option>
              </select>

              <select
                className="bg-white bg-opacity-90 border-none px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2
                  rounded-xl sm:rounded-2xl text-gray-700 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm cursor-pointer flex-1 sm:flex-initial"
                value={selectedBarrette}
                onChange={(e) => {
                  setSelectedBarrette(e.target.value);
                  if (e.target.value !== 'all') setSelectedNiveau('all');
                }}
              >
                <option value="all">Barrette</option>
                {barrettesUniques.map((barrette) => (
                  <option key={barrette} value={barrette}>
                    {barrette.split(',').join(', ')}
                  </option>
                ))}
              </select>

              {/* Filtre matière — uniquement pour le délégué (qui voit les 2 matières) */}
              {isDelegueAffectation && (
                <select
                  className="bg-white bg-opacity-90 border-none px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2
                    rounded-xl sm:rounded-2xl text-gray-700 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm cursor-pointer flex-1 sm:flex-initial"
                  value={selectedMatiere}
                  onChange={(e) => setSelectedMatiere(e.target.value)}
                >
                  <option value="all">Toutes les matières</option>
                  <option value="maths">Maths</option>
                  <option value="Français">Français</option>
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 xl:gap-4 2xl:gap-5 3xl:gap-6 pt-4 pb-4 xl:pt-3 xl:pb-3 2xl:pt-3.5 2xl:pb-3.5 3xl:pt-4 3xl:pb-4 px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-white mx-3 sm:mx-5">
          <div className="bg-white rounded-xl p-4 sm:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
            <div className="text-2xl sm:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-blue-500 mb-1 sm:mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2">{groupesDeBesoin.length}</div>
            <div className="text-[10px] sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-medium text-gray-600 uppercase tracking-wide">Groupes créés</div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
            <div className="text-2xl sm:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-green-500 mb-1 sm:mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2">{getTotalEleves()}</div>
            <div className="text-[10px] sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-medium text-gray-600 uppercase tracking-wide">Élèves total</div>
          </div>
        </div>

        {/* TABLE VIEW (desktop only) */}
        {!isMobile && currentView === 'table' && (
          <div className="py-2 justify-center mb-20 px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-white mx-3 sm:mx-5">
            <div className=" rounded-2xl">
              <table className="min-w-full sm:min-w-[960px]">
                <thead className="bg-gradient-to-r from-blue-500 to-sky-600 text-white">
                  <tr>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Nom du groupe</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Niveau</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Classe d'origine</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Nb élèves</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Professeur</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Statut</th>
                    <th className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredGroupes.map((groupe) => (
                    <TableGroupe
                      key={groupe.id}
                      groupe={groupe}
                      editingGroup={editingGroupe}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onViewStudents={handleViewStudents}
                      cancelEditing={handleEditMode}
                      profDetail={etablissementData?.professeurs.filter((p) => p.matiere === user.matiere)}
                      editedProfesseur={editedProfesseur}
                      setEditedProfesseur={setEditedProfesseur}
                      editedNomGroupe={editedNomGroupe}
                      setEditedNomGroupe={setEditedNomGroupe}
                      handleUpdateGroupe={handleUpdateGroupe}
                      handleDetailGroupeClick={handleDetailGroupeClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CARDS VIEW (mobile par défaut) */}
        {(isMobile || currentView === 'cards') && (
          <div className="grid bg-white py-4 px-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 mb-20 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 xl:gap-3 2xl:gap-4 3xl:gap-5 mx-3 sm:mx-5">
            {filteredGroupes.length > 0 ? (
              filteredGroupes.map(groupe => (
                <CardGroup
                  key={groupe.id}
                  groupe={groupe}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewStudents={handleViewStudents}
                  editingGroup={editingGroupe}
                  cancelEditing={handleEditMode}
                  profDetail={etablissementData?.professeurs.filter((p) => p.matiere === user.matiere)}
                  editedProfesseur={editedProfesseur}
                  setEditedProfesseur={setEditedProfesseur}
                  editedNomGroupe={editedNomGroupe}
                  setEditedNomGroupe={setEditedNomGroupe}
                  handleUpdateGroupe={handleUpdateGroupe}
                  handleDetailGroupeClick={handleDetailGroupeClick}
                />
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 xl:w-16 xl:h-16 2xl:w-18 2xl:h-18 3xl:w-20 3xl:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Layers className="w-8 h-8 sm:w-10 sm:h-10 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-gray-400" />
                </div>
                <h3 className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-bold text-gray-700 mb-2">Aucun groupe trouvé</h3>
                <p className="text-gray-500 text-center max-w-md text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                  {searchTerm || selectedNiveau !== 'all' || selectedStatut !== 'all'
                    ? "Aucun groupe ne correspond à vos critères de recherche."
                    : "Commencez par créer votre premier groupe de besoins."}
                </p>
              </div>
            )}
          </div>
        )}

        {showModal && (
          <AddGroupe
            profDetail={isDelegueAffectation
              ? (etablissementData?.professeurs || [])
              : (etablissementData?.professeurs?.filter((p) => p.matiere === user.matiere) || [])}
            isDelegueAffectation={isDelegueAffectation}
            show={showModal}
            handleAddGroupe={handleAddGroupe}
            handleClose={handleClose}
            listClasses={etablissementData.classes}
            selectedClasses={selectedClasses}
            handleCheckClasseChange={handleCheckClasseChange}
            niveau={niveau}
            handleRadioChange={handleRadioChange}
            handleDetailGroupeClick={handleDetailGroupeClick}
          />
        )}

        {showImportModal && (
          <ImportElevesGroupe
            show={showImportModal}
            handleClose={() => setShowImportModal(false)}
            // Groupes de besoin SEULEMENT (décision prof 2026-09-30) : les
            // demi-groupes se composent par leurs profs, l'import n'y touche
            // plus. Un « 6G_G1 » du fichier est ignoré et listé comme une langue
            // ou une option (utils/importGroupes.js). Le reconnaître faisait
            // partir un élève par sa seule ligne de demi-groupe : le serveur le
            // sortait alors de son groupe de besoin si celui-ci était mal écrit
            // (cas vécu le 2026-09-30 : 6D_G1 reconnu, 6DE_MATH2 inconnu).
            listGroupes={groupesDeBesoin}
            etablissementData={etablissementData}
            onSuccess={() => setReload(!reload)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <DetailGroupe
        handleDetailGroupeClick={handleDetailGroupeClick}
        selectedGroupe={selectedGroupe}
        // Les cibles de « changer de groupe » : groupes de besoin seulement.
        // Viser un demi-groupe AJOUTE l'élève sans le retirer de sa barrette
        // (reassigner_eleve, back) : l'écran dirait « déplacé » à tort.
        listGroupes={groupesDeBesoin}
        handleUpdateEleveGroupe={handleUpdateEleveGroupe}
      />
      {ConfirmDialog}
    </>
  );
}

export default AdminGroupe;
